import { REALADVISOR_TENANT_IDS } from '@realadvisor/constants';

import type { MeQuery } from '@graphql/__generated__/graphql';

export type Me =
  | (MeQuery['get_me']['me'] & {
      show_plans: MeQuery['get_me']['show_plans'];
      claimed_at: MeQuery['get_me']['claimed_at'];
      user_ids_in_led_teams: NonNullable<
        MeQuery['get_me']['user_ids_in_led_teams']
      >;
    })
  | null;

export type AppData = {
  me: Me;
  noAccess?: { userId: string };
  needsOnboarding?: { userId: string };
  // JWT `x-hasura-default-role`: the role every CRM request runs under.
  hasuraRole?: string | null;
};

type ResolveAppDataInput = {
  accessToken: string | null;
  // The decoded access token. Untyped like the `parseJwt` result the provider
  // holds - narrowing it here would change what `noAccess.userId` is allowed
  // to be, which is a separate change from lifting this logic out.
  decodedToken: any;
  meData: MeQuery | undefined;
  isMultitenantRole: boolean;
  isNotAdminNorBroker: boolean;
  meQuerySkipped: boolean;
};

// The three answers the app shell can be given: the user, a denial, or
// nothing known yet. `BaseApp` reads them in that order, so which one comes
// back decides whether someone sees the CRM, /noaccess, or the login card.
export const resolveAppData = ({
  accessToken,
  decodedToken,
  meData,
  isMultitenantRole,
  isNotAdminNorBroker,
  meQuerySkipped,
}: ResolveAppDataInput): AppData => {
  const claims = decodedToken?.['https://hasura.io/jwt/claims'];
  const role = claims?.['x-hasura-default-role'];
  const hasuraRole: string | null = typeof role === 'string' ? role : null;

  if (meData?.get_me.me == null || meData.get_me.me.tenant == null) {
    if (accessToken == null || isMultitenantRole) {
      return {
        me: null,
      };
    }
    // The token itself says this user is neither an admin nor a broker, so
    // this is a real denial and needs no query to confirm.
    if (isNotAdminNorBroker) {
      const tokenTenantId =
        decodedToken?.user?.tenantId ?? claims?.['x-hasura-tenant-id'];
      if (
        typeof tokenTenantId === 'string' &&
        !REALADVISOR_TENANT_IDS.has(tokenTenantId)
      ) {
        return {
          noAccess: { userId: decodedToken.user?.id },
          me: null,
        };
      }
      return {
        me: null,
        needsOnboarding: { userId: decodedToken.user?.id },
      };
    }
    // Otherwise denial has to come from an answer, not from the absence of
    // one. `meData` is equally undefined when the query was merely skipped
    // for the current route (/login, /select-tenant), and treating that as
    // "no access" sent fully authorised users to /noaccess until they
    // reloaded. Without a completed query we know nothing, so assert
    // neither access nor denial.
    if (meQuerySkipped) {
      return {
        me: null,
      };
    }
    return {
      noAccess: { userId: decodedToken.user?.id },
      me: null,
    };
  }

  const me = {
    ...meData.get_me.me,
    show_plans: meData.get_me.show_plans,
    claimed_at: meData.get_me.claimed_at ?? null,
    user_ids_in_led_teams: meData.get_me.user_ids_in_led_teams ?? [],
  };

  return me.is_admin !== true &&
    me.has_done_onboarding === false &&
    REALADVISOR_TENANT_IDS.has(meData.get_me.me.tenant.id)
    ? { me, needsOnboarding: { userId: me.id }, hasuraRole }
    : { me, hasuraRole };
};
